import { Link } from 'react-router-dom';
import { CopyButton } from '../components/CopyButton';

const API_BASE = 'https://astdz-api.astdz.workers.dev';

interface Endpoint {
  method: string;
  path: string;
  auth: 'public' | 'session' | 'session/key' | 'API key' | 'owner/admin';
  description: string;
}

const ENDPOINTS: Endpoint[] = [
  { method: "GET", path: "/v1/health", auth: "public", description: "service status" },
  { method: "POST", path: "/v1/validate", auth: "public", description: "validate any of the 11 schema payloads; supply context when required" },
  { method: "GET", path: "/v1/messages", auth: "public", description: "search and filter the message library" },
  { method: "GET", path: "/v1/messages/:ref", auth: "public", description: "get one message by decimal or hex reference" },
  { method: "GET", path: "/v1/schemas", auth: "public", description: "standards registry and implementation status" },
  { method: "GET", path: "/v1/schemas/:code", auth: "public", description: "schema and implemented tier layout" },
  { method: "GET", path: "/v1/certs/verify/:orgId", auth: "public", description: "active certificate records" },
  { method: "POST", path: "/v1/auth/signup", auth: "public", description: "create owner account and session; terms acceptance required" },
  { method: "POST", path: "/v1/auth/login", auth: "public", description: "log in; returns session token" },
  { method: "GET", path: "/v1/auth/me", auth: "session", description: "current account and member schema access" },
  { method: "POST", path: "/v1/auth/logout", auth: "session", description: "revoke the presented session token" },
  { method: "POST", path: "/v1/auth/change-password", auth: "session", description: "change password using current password" },
  { method: "GET", path: "/v1/content/codecs", auth: "session", description: "complete field metadata and examples for all 11 codecs" },
  { method: "GET", path: "/v1/content/codec.mjs", auth: "session", description: "download the complete JavaScript reference SDK" },
  { method: "POST", path: "/v1/encode", auth: "session/key", description: "encode any schema within matching certification, scope and tier" },
  { method: "POST", path: "/v1/decode", auth: "session/key", description: "decode and log any schema within matching certification, scope and tier" },
  { method: "GET", path: "/v1/devices", auth: "session/key", description: "list permitted organization devices" },
  { method: "POST", path: "/v1/devices", auth: "session/key", description: "register a device within certified schema/tier" },
  { method: "GET", path: "/v1/devices/:deviceId", auth: "session/key", description: "device and activity within organization" },
  { method: "GET", path: "/v1/certs", auth: "session/key", description: "organization certificate records" },
  { method: "POST", path: "/v1/certs/request", auth: "owner/admin", description: "request a test or production certificate for a schema" },
  { method: "POST", path: "/v1/certs/:id/renew", auth: "owner/admin", description: "renew active certificate" },
  { method: "GET", path: "/v1/keys", auth: "session", description: "visible key metadata; no plaintext" },
  { method: "POST", path: "/v1/keys", auth: "session", description: "mint a scoped API key" },
  { method: "POST", path: "/v1/keys/:id/rotate", auth: "session", description: "rotate with up to seven days of overlap" },
  { method: "DELETE", path: "/v1/keys/:id", auth: "owner/admin", description: "revoke a key immediately" },
  { method: "GET", path: "/v1/keys/:id/usage", auth: "session", description: "last-use metadata" },
  { method: "POST", path: "/v1/bridge/ingest", auth: "API key", description: "decode and log any schema; outbound Fastalert forwarding is EMRG-only" },
  { method: "GET", path: "/v1/inbox", auth: "session/key", description: "permitted activity; use nextSince and nextId cursor" },
  { method: "GET", path: "/v1/users", auth: "owner/admin", description: "list organization members" },
  { method: "GET", path: "/v1/users/invitations", auth: "owner/admin", description: "list pending invitations" },
  { method: "POST", path: "/v1/users/invite", auth: "owner/admin", description: "create a shareable invitation token" },
  { method: "POST", path: "/v1/users/invitations/:id/revoke", auth: "owner/admin", description: "revoke pending invitation" },
  { method: "POST", path: "/v1/users/accept-invite", auth: "public", description: "accept token with password; returns session" },
  { method: "POST", path: "/v1/users/:id/disable", auth: "owner/admin", description: "disable member and revoke sessions" },
  { method: "POST", path: "/v1/users/:id/reenable", auth: "owner/admin", description: "reactivate disabled member" },
  { method: "PUT", path: "/v1/users/:id/schemas", auth: "owner/admin", description: "replace member schema allowlist" },
];

const SAMPLE_CURL = `curl -X POST ${API_BASE}/v1/encode \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -d '{"schema":"EMRG","eventCategory":"01","severity":3,"status":"01","deviceId":1}'`;

const FAMILY_EXAMPLE = `const token = '<SESSION_TOKEN>';
const headers = { Authorization: \`Bearer \${token}\` };
const registry = await fetch('${API_BASE}/v1/content/codecs', { headers }).then(r => r.json());

const example = registry.examples.find(item => item.input.schema === 'ATMO' && item.input.tier === 0);
const definition = registry.definitions.find(item =>
  item.code === example.input.schema && item.tier === example.input.tier
);
console.table(definition.fields); // inspect accepted keys, units, storage, and nullability

const result = await fetch('${API_BASE}/v1/encode', {
  method: 'POST',
  headers: { ...headers, 'Content-Type': 'application/json' },
  body: JSON.stringify({ ...example.input, context: example.context }),
}).then(r => r.json());
console.log(result.hex);`;

/** Route: `/dashboard/api`. REST base URL, sample curl, and endpoint table. */
export function ApiReferencePage() {
  const curl = SAMPLE_CURL;

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── REST API ──────────────────────────────────────</div>

      <div className="box" style={{ marginBottom: 16 }}>
        <div className="form-row">
          <span className="label">base URL</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <pre className="hex-display" style={{ flex: 1, margin: 0 }}>{API_BASE}</pre>
            <CopyButton text={API_BASE} label="copy" />
          </div>
        </div>

        <hr className="divider" />

        <div className="form-row">
          <span className="label">your API key</span>
          <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>
            request certification for the schema and tier you will use. API keys are then minted and copied once on the <Link to="/dashboard/keys">keys page</Link>.
            paste your key in place of <code>&lt;YOUR_API_KEY&gt;</code> in the sample below.
          </p>
        </div>

        <hr className="divider" />

        <div className="form-row">
          <span className="label">example: encode a payload</span>
          <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto' }}>{curl}</pre>
          <div style={{ marginTop: 8 }}>
            <CopyButton text={curl} label="copy curl" />
          </div>
        </div>
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── family codec example ───────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <p>Use an account session to inspect available input metadata and examples, then encode with the example context. Supplied assessment context constrains a claim; it does not verify the assessor's identity or authority.</p>
        <pre className="hex-display" style={{ whiteSpace: 'pre', overflowX: 'auto' }}>{FAMILY_EXAMPLE}</pre>
        <CopyButton text={FAMILY_EXAMPLE} label="copy JavaScript" />
      </div>

      <div className="text-muted" style={{ marginBottom: 8 }}>── endpoints ─────────────────────────────────────</div>
      <div className="box" style={{ marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>method</th>
              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>path</th>
              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>auth</th>
              <th style={{ textAlign: 'left', padding: '4px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-muted)', fontWeight: 500 }}>description</th>
            </tr>
          </thead>
          <tbody>
            {ENDPOINTS.map((e) => (
              <tr key={`${e.method} ${e.path}`}>
                <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)', color: 'var(--color-accent)' }}>{e.method}</td>
                <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)' }}>{e.path}</td>
                <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)', color: 'var(--color-muted)' }}>{e.auth}</td>
                <td style={{ padding: '4px 8px', borderBottom: '1px dashed var(--color-border)' }}>{e.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p><Link to="/docs/api">→ full API reference</Link> · <Link to="/docs/standards">standards catalog</Link> · <Link to="/docs/standards/emrg">complete EMRG specification</Link></p>
    </div>
  );
}
